import React from "react";
import { motion } from "framer-motion";
import Section from "../components/Section";
import CTA from "../components/CTA";
import { PageHeader } from "./Platform";
import { IMG } from "../lib/images";

const useCases = [
  {
    id: "luxury",
    title: "Luxury Hotels",
    img: IMG.suiteView,
    challenges: [
      "Guests expect concierge-level personalisation on every touchpoint.",
      "Front-desk waits erode the arrival experience.",
      "Multilingual guests demand tone-perfect service.",
    ],
    solutions: [
      "Digital pre check-in + digital key for a frictionless arrival.",
      "Multilingual guest app with in-context recommendations.",
      "Real-time request tracking with SLA-bound escalations.",
    ],
  },
  {
    id: "business",
    title: "Business Hotels",
    img: IMG.businessHotel,
    challenges: [
      "Business travellers optimise for speed. Every minute matters.",
      "High turnover volume stresses housekeeping and F&B.",
      "Repeat guests expect their preferences remembered.",
    ],
    solutions: [
      "Fast pre check-in, mobile keys, and one-tap requests.",
      "Task orchestration across housekeeping, F&B, and engineering.",
      "Guest profile signals to personalise the next stay.",
    ],
  },
  {
    id: "chains",
    title: "Hotel Chains",
    img: IMG.chainBuilding,
    challenges: [
      "Standards drift across properties without a single system.",
      "Reporting is manual, delayed, and inconsistent.",
      "IT rollouts stall on legacy integrations.",
    ],
    solutions: [
      "Cloud-native platform with multi-property HOP dashboards.",
      "Consistent SLAs, benchmarks, and audit-ready operations.",
      "Certified PMS/POS integrations, deployed in days.",
    ],
  },
  {
    id: "resorts",
    title: "Resorts",
    img: IMG.poolResort,
    challenges: [
      "Sprawling grounds make staff coordination hard.",
      "Guests expect activities, dining, and services in one place.",
      "Occupancy peaks strain every department at once.",
    ],
    solutions: [
      "Zone-aware staff routing and live location signals.",
      "Unified guest app for dining, spa, activities and offers.",
      "Peak-hour SLA visibility that keeps every desk green.",
    ],
  },
  {
    id: "serviced",
    title: "Serviced Apartments",
    img: IMG.servicedApt,
    challenges: [
      "Long-stay guests expect continuous, low-friction service.",
      "Lean front-desk teams cover 24×7 operations.",
      "Housekeeping cycles differ per unit and per stay length.",
    ],
    solutions: [
      "Digital-first check-in and continuous mobile requests.",
      "Automated task orchestration with escalation matrices.",
      "Per-unit housekeeping automations and inventory tracking.",
    ],
  },
  {
    id: "boutique",
    title: "Boutique Hotels",
    img: IMG.boutiqueRoom,
    challenges: [
      "A small team wears many hats and cannot miss a request.",
      "Brand voice is the product — service must feel bespoke.",
      "Reviews shape occupancy for months.",
    ],
    solutions: [
      "Single connected inbox for every guest signal.",
      "Brand-voice-ready multilingual communication.",
      "Digital feedback loops that surface issues in-stay, not after.",
    ],
  },
  {
    id: "hospital",
    title: "Hospital Groups",
    img: IMG.hospital,
    challenges: [
      "Patient and attendant expectations are hospitality-grade.",
      "Coordination between clinical, F&B, and housekeeping is complex.",
      "Compliance and audit trails cannot be optional.",
    ],
    solutions: [
      "Guest-style app for patients, families, and attendants.",
      "Cross-department orchestration with SLA governance.",
      "ISO-aligned platform with audit-ready operations.",
    ],
  },
];

export default function UseCases() {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="page-use-cases"
    >
      <PageHeader
        overline="Use cases"
        title={
          <>
            Built for every kind of{" "}
            <span className="grad-text">hospitality operation.</span>
          </>
        }
        subtitle="One platform. Seven distinct property archetypes. Every deployment configured to how your team already works."
      />

      <Section overline="Sectors" testId="use-cases-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {useCases.map((u, i) => (
            <motion.article
              key={u.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="group relative rounded-2xl overflow-hidden brand-panel hover:border-brand-purpleLight/60 transition-colors"
              data-testid={`use-case-${u.id}`}
            >
              <div className="relative h-56 overflow-hidden">
                <div className="absolute inset-0 img-treated">
                  <img
                    src={u.img}
                    alt={u.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                  />
                </div>
                <div className="relative z-10 p-6 h-full flex items-end">
                  <h3
                    className="font-display text-white text-[32px] md:text-[40px] leading-tight"
                    style={{ fontWeight: 800 }}
                  >
                    {u.title}
                  </h3>
                </div>
              </div>
              <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="font-display text-[10px] uppercase tracking-[0.28em] text-brand-orange mb-3 font-bold">
                    Challenges
                  </div>
                  <ul className="space-y-2">
                    {u.challenges.map((c, idx) => (
                      <li
                        key={idx}
                        className="text-white/75 text-[13.5px] leading-relaxed"
                      >
                        · {c}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="font-display text-[10px] uppercase tracking-[0.28em] text-white mb-3 font-bold">
                    How iNPLASS solves
                  </div>
                  <ul className="space-y-2">
                    {u.solutions.map((s, idx) => (
                      <li
                        key={idx}
                        className="text-white text-[13.5px] leading-relaxed"
                      >
                        · {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </Section>

      <CTA />
    </motion.main>
  );
}
